import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router, NavigationEnd} from '@angular/router';
import {BehaviorSubject, Subscription} from 'rxjs';
import {FloatingButtonAction, TypeAction} from '@share/component/floating-button/floating-button';
import {ContratService} from '@services/admin/contrat/contrat.service';
import {ContratButtonService} from '@services/admin/contrat/contrat.button.service';
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {FraisFacturesService} from '@components/admin/frais/factures/frais-factures.service';
import {filter, first} from 'rxjs/operators';
import {Contrat, TypeDebit, TypeImport} from '@domain/contrat/contrat';

/**
 * Composant de gestion des informations d'un contrat
 *
 * @author Yannick Gauthier
 * @date 18/08/2025
 */
@Component({
    host: {'data-test-id': 'contrat-infos'},
    selector: 'contrat-infos',
    templateUrl: './contrat-infos.component.html'
})
export class ContratInfosComponent implements OnInit, OnDestroy {
    /** Liste des onglets */
    listeTabItems: Array<PageHeaderItem>;
    
    /** Liste des actions */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>([]);
    
    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;
    
    /** Souscription aux actions */
    private actionsSubscription: Subscription;
    
    /** Souscription aux événements de navigation */
    private navigationSubscription: Subscription;
    
    /** Onglet actuellement sélectionné */
    private currentTab: string = '';

    /** Contrat courant */
    private contrat: Contrat;

    /**
     * Constructeur
     * @param router Service de routage
     * @param activatedRoute Route activée
     * @param contratService Service de gestion des contrats
     * @param contratButtonService Service de gestion des boutons
     * @param fraisFacturesService Service de gestion des frais et factures
     */
    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private contratService: ContratService,
        private contratButtonService: ContratButtonService,
        private fraisFacturesService: FraisFacturesService,
    ) {}

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération des paramètres de la route
        const idContrat = this.activatedRoute.snapshot.params.idContrat;
        const idFournisseur = this.activatedRoute.snapshot.queryParams.idFournisseur;
        
        //Indique au composant Frais Factures qu'on affiche le détail d'un contrat
        this.fraisFacturesService.isShowDetail = true;
        
        //Souscription aux actions
        this.actionsSubscription = this.contratButtonService.getActions().subscribe(actions => {
            this.listeActions.next(actions);
        });
        
        //Chargement du contrat pour déterminer les onglets à afficher
        this.loadContrat(idContrat, idFournisseur);
        
        //Souscription aux événements de navigation pour détecter les changements d'onglet
        this.navigationSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                //Détermination de l'onglet actuel à partir de l'URL
                const url = this.router.url;
                let newTab = '';
                
                if (url.includes('/Generalites')) {
                    newTab = TypeOnglet.GENERALITES;
                } else if (url.includes('/Comptes')) {
                    newTab = TypeOnglet.COMPTES;
                }
                
                //Si l'onglet a changé, on force la mise à jour des actions
                if (newTab !== this.currentTab) {
                    this.currentTab = newTab;
                    
                    //Réinitialisation des actions pour forcer leur mise à jour par le composant enfant
                    this.contratButtonService.setActions([]);
                    
                    //Petit délai pour laisser le temps au composant enfant de s'initialiser
                    setTimeout(() => {
                        //Si on est sur l'onglet Comptes et qu'aucune action n'a été définie
                        if (this.currentTab === TypeOnglet.COMPTES && !this.listeActions.value.length) {
                            //Définition des actions par défaut pour l'onglet Comptes
                            const idContrat = this.activatedRoute.snapshot.params.idContrat;
                            const idFournisseur = this.activatedRoute.snapshot.queryParams.idFournisseur;
                            
                            this.contratButtonService.setActions([{
                                type: TypeAction.PRIMARY,
                                icone: 'nio icon-ajouter',
                                libelle: 'global.actions.ajouter',
                                doAction: () => this.router.navigate(['Admin/Frais/Factures/Contrat', idContrat, 'Compte', 0], {
                                    queryParams: { idFournisseur: idFournisseur }
                                })
                            }]);
                        }
                    }, 100);
                }
            });
        
        //Détection de l'onglet initial
        const url = this.router.url;
        if (url.includes('/Generalites')) {
            this.currentTab = TypeOnglet.GENERALITES;
        } else if (url.includes('/Comptes')) {
            this.currentTab = TypeOnglet.COMPTES;
        }
    }

    /**
     * Charge les données du contrat et initialise les onglets
     * @param idContrat ID du contrat
     * @param idFournisseur ID du fournisseur
     */
    private loadContrat(idContrat: number, idFournisseur: number): void {
        //Si on est en création, on n'affiche que l'onglet Généralités
        if (!idContrat || idContrat <= 0) {
            this.initOnglets(idContrat, idFournisseur, false);
            return;
        }

        //Chargement du contrat
        this.contratService.getContrat(idContrat).pipe(first()).subscribe({
            next: result => {
                if (result && result.data && result.data.contrat) {
                    this.contrat = result.data.contrat;
                    
                    //Vérification de la condition pour afficher l'onglet COMPTES
                    const showComptesTab = this.contrat.typeImport === TypeImport.CARTE || this.contrat.typeDebit === TypeDebit.COLLAB;
                    
                    //Initialisation des onglets
                    this.initOnglets(idContrat, idFournisseur, showComptesTab);
                } else {
                    //Si pas de contrat, on n'affiche que l'onglet Généralités
                    this.initOnglets(idContrat, idFournisseur, false);
                }
            },
            error: () => {
                //En cas d'erreur, on n'affiche que l'onglet Généralités
                this.initOnglets(idContrat, idFournisseur, false);
            }
        });
    }

    /**
     * Initialise les onglets en fonction des conditions
     * @param idContrat ID du contrat
     * @param idFournisseur ID du fournisseur
     * @param showComptesTab Indique si l'onglet COMPTES doit être affiché
     */
    private initOnglets(idContrat: number, idFournisseur: number, showComptesTab: boolean): void {
        //Initialisation de l'onglet GENERALITES
        this.listeTabItems = [{
            code: TypeOnglet.GENERALITES,
            libelle: 'admin.frais.fournisseurs.contrat.onglets.generalites',
            url: 'Admin/Frais/Factures/Contrat/' + idContrat + '/Generalites?idFournisseur=' + idFournisseur
        }];
        
        //Ajout de l'onglet COMPTES si nécessaire
        if (showComptesTab) {
            this.listeTabItems.push({
                code: TypeOnglet.COMPTES,
                libelle: 'admin.frais.fournisseurs.contrat.onglets.comptes',
                url: 'Admin/Frais/Factures/Contrat/' + idContrat + '/Comptes?idFournisseur=' + idFournisseur
            });
        } else if (this.currentTab === 'COMPTES') {
            //Si l'onglet courant est COMPTES mais qu'il ne doit plus être affiché, on redirige vers GENERALITES
            this.router.navigate(['Admin/Frais/Factures/Contrat', idContrat, 'Generalites'], {
                queryParams: { idFournisseur: idFournisseur }
            });
        }
    }

    /**
     * Gestion du changement d'onglet
     * @param item Onglet sélectionné
     */
    onTabChange(item: PageHeaderItem): void {
        if (item && item.url) {
            this.router.navigateByUrl(item.url);
        }
    }

    /**
     * Retour à la liste des contrats
     */
    goBack(): void {
        //Vérifier si une fonction de retour personnalisée est définie
        if (!this.contratButtonService.executeCustomGoBackFunction()) {
            //Si aucune fonction personnalisée n'est définie, utiliser le comportement par défaut
            const idFournisseur = this.activatedRoute.snapshot.queryParams.idFournisseur;
            //Naviguer vers la liste des contrats du fournisseur
            this.router.navigate(['Admin/Frais/Factures/Fournisseurs', idFournisseur], {
                fragment: 'CONTRAT'
            });
        }
    }

    /**
     * Hook destruction
     */
    ngOnDestroy(): void {
        //Indique au composant Frais Factures qu'on n'affiche plus le détail d'un contrat
        this.fraisFacturesService.isShowDetail = false;
        
        //Désabonnement
        if (this.actionsSubscription) {
            this.actionsSubscription.unsubscribe();
        }
        
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }
}

/**
 * Enum pour les types d'onglets du contrat
 */
export enum TypeOnglet {
    GENERALITES = 'GENERALITES',
    COMPTES = 'COMPTES'
}
